import { inject, Injectable } from '@angular/core';

import {
  ExtractedDocumentDataSnapshot,
  PersistDataService,
} from '../app-state/persist-data.service';
import {
  ById,
  ExtractedRow,
  ExtractedTablesByKey,
  ExtractedValuesByKey,
} from '../../models/extracted-values';
import {
  sectionsByDomainAndDocumentClass,
  TableKey,
} from '../../models/extraction-models/extraction-models';
import { BatchDomain, DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { isFieldGroupModel, isTableModel } from '../../models/domain-extraction-specifications';

@Injectable({
  providedIn: 'root',
})
export class ExtractedDataStoreService {
  private readonly persistDataService = inject(PersistDataService);

  private _batchTablesByDocumentId: ById<ExtractedTablesByKey> = {};
  private _batchFieldsByDocumentId: ById<ExtractedValuesByKey> = {};

  constructor() {
    this._batchFieldsByDocumentId = this.persistDataService.extractedFields ?? {};
  }

  async deleteExtractedDataForDocument(
    documentId: string,
    domain: BatchDomain,
    documentClass: DocumentClassKey,
  ) {
    const sections = sectionsByDomainAndDocumentClass[domain][documentClass] ?? [];

    for (const section of sections) {
      if (isFieldGroupModel(section)) {
        for (const fieldModel of section.fieldModels) {
          delete this._batchFieldsByDocumentId[documentId]?.[fieldModel.key];
        }
      } else if (isTableModel(section)) {
        delete this._batchTablesByDocumentId[documentId]?.[section.key];
      }
    }

    this.persistDataService.extractedFields = this._batchFieldsByDocumentId;
    await this.persistDataService.persistTables(
      documentId,
      this._batchTablesByDocumentId[documentId] ?? {},
    );
    await this.persistDataService.saveExtractedDocumentDataSnapshot(documentId, documentClass, {
      fields: {},
      tables: {},
    });
    this.persistDataService.setUseTrainingDataForDocument(documentId, false);
  }

  storeExtractedBatchFields(documentId: string, valuesByKey: ExtractedValuesByKey) {
    this._batchFieldsByDocumentId[documentId] = {
      ...(this._batchFieldsByDocumentId[documentId] ?? {}),
      ...valuesByKey,
    };
    this.persistDataService.extractedFields = this._batchFieldsByDocumentId;
  }

  getExtractedBatchFields(documentId: string): Partial<ExtractedValuesByKey> {
    return this._batchFieldsByDocumentId[documentId] ?? {};
  }

  hasFieldsForDocument(documentId: string): boolean {
    return documentId in this._batchFieldsByDocumentId;
  }

  clearExtractedTables() {
    this._batchTablesByDocumentId = {};
  }

  storeExtractedRows(documentId: string, tableModelKey: TableKey, extractedRows: ExtractedRow[]) {
    const filteredRowValues = extractedRows.filter((rowData) =>
      Object.values(rowData.rowData).some((row) => row?.value !== null && row?.value !== ''),
    );

    this.saveExtractedBatchTables(documentId, tableModelKey, filteredRowValues);
  }

  private saveTablesInMemory(documentId: string, tableKey: TableKey, rows: ExtractedRow[]) {
    if (!(documentId in this._batchTablesByDocumentId)) {
      this._batchTablesByDocumentId[documentId] = {};
    }
    this._batchTablesByDocumentId[documentId]![tableKey] = rows;
  }

  saveExtractedBatchTables(documentId: string, tableKey: TableKey, rows: ExtractedRow[]) {
    this.saveTablesInMemory(documentId, tableKey, rows);

    this.persistDataService.addPersistTablesEvent(
      documentId,
      this._batchTablesByDocumentId[documentId] ?? {},
    );
  }

  async saveExtractedBatchTablesImmediately(
    documentId: string,
    key: string,
    extractedRows: ExtractedRow[],
  ) {
    this.saveTablesInMemory(documentId, key, extractedRows);

    return this.persistDataService.persistTables(
      documentId,
      this._batchTablesByDocumentId[documentId] ?? {},
    );
  }

  async doTableRowsExist(documentId: string, tableKey: string): Promise<boolean> {
    const extractedRowsForTable = await this.getExtractedRowsForTable(documentId, tableKey);
    return extractedRowsForTable.length > 0;
  }

  async getExtractedTables(documentId: string): Promise<ExtractedTablesByKey> {
    if (!(documentId in this._batchTablesByDocumentId)) {
      const tablesForDocument = await this.persistDataService.getExtractedTables(documentId);
      this._batchTablesByDocumentId[documentId] = tablesForDocument ?? {};
    }

    return this._batchTablesByDocumentId[documentId] ?? {};
  }

  async getExtractedRowsForTable(documentId: string, tableKey: TableKey): Promise<ExtractedRow[]> {
    const tables = await this.getExtractedTables(documentId);
    return tables[tableKey] ?? [];
  }

  /**
   * Here we assume that the current extracted data is always in the in-memory storage of this service
   * when we request a snapshot.
   * Especially, this method does NOT query the index-db, so requesting a snapshot here
   * for a documentId whose tables where not loaded yet will return invalid data.
   */
  getExtractedDocumentDataSnapshot(documentId: string): ExtractedDocumentDataSnapshot {
    return {
      fields: this._batchFieldsByDocumentId[documentId] ?? {},
      tables: this._batchTablesByDocumentId[documentId] ?? {},
    };
  }
}
